/**
 * SubgroupTree — tree component for selecting descendant subgroups in Step 1
 * of the CascadeSettingsModal. Renders a scrollable tree of group rows, each
 * with two checkboxes (Update Group, Include Projects), with expand/collapse
 * for groups that have children.
 */
import { FC, memo, useMemo, useState } from "react"
import {
    Checkbox,
    colorFunctionalGray10,
    colorFunctionalGray20,
    colorFunctionalGray50,
    colorFunctionalGray70,
    spacingXs,
} from "@rhumbix/rmbx_design_system_web"
import styled, { css } from "styled-components"
import { iCompanyGroup } from "../../../cached-data/types"
import { TREE_CHECKBOX_COL_WIDTH, TREE_INDENT_PX } from "./constants"
import { SubgroupTreeNodeProps } from "./types"

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

/** Shared grid layout for the subgroup tree header and rows. */
const treeGridColumns = css`
    display: grid;
    grid-template-columns: 1fr ${TREE_CHECKBOX_COL_WIDTH}px ${TREE_CHECKBOX_COL_WIDTH}px;
    align-items: center;
    padding: 6px 10px;
`

export const TreeBody = styled.div`
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: none;
    border: 1px solid ${colorFunctionalGray20};
    border-radius: 4px;
`

export const TreeHeader = styled.div`
    ${treeGridColumns}
    background: ${colorFunctionalGray10};
    border-bottom: 1px solid ${colorFunctionalGray20};
    font-size: 11px;
    font-weight: 700;
    color: ${colorFunctionalGray50};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    position: sticky;
    top: 0;
    z-index: 1;
`

export const TreeRow = styled.div`
    ${treeGridColumns}
    border-bottom: 1px solid ${colorFunctionalGray10};

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: ${colorFunctionalGray10};
    }
`

export const TreeNameCell = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: ${spacingXs};
    overflow: hidden;
`

export const TreeHeaderNameCell = styled.div`
    flex: 1;
    min-width: 0;
`

export const TreeHeaderCheckboxCell = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    cursor: pointer;
    text-align: center;
    && svg {
        position: static;
    }
`

export const TreeCheckboxCell = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`

const ChildrenWrapper = styled.div<{ $expanded: boolean }>`
    display: grid;
    grid-template-rows: ${({ $expanded }) => ($expanded ? "1fr" : "0fr")};
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    transition: grid-template-rows 0.2s ease, opacity 0.2s ease;
`

/** Inner container required by the grid-template-rows collapse technique. */
const ChildrenInner = styled.div`
    overflow: hidden;
    min-height: 0;
`

const ExpandToggle = styled.span<{ $expanded: boolean }>`
    display: inline-block;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    cursor: pointer;
    position: relative;

    &::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        width: 6px;
        height: 6px;
        border-right: 2px solid ${colorFunctionalGray50};
        border-bottom: 2px solid ${colorFunctionalGray50};
        transform: translate(-70%, -50%) rotate(${({ $expanded }) => ($expanded ? "45deg" : "-45deg")});
        transition: transform 0.15s ease;
    }

    &:hover::before {
        border-color: ${colorFunctionalGray70};
    }
`

const GroupNameText = styled.span`
    font-size: 13px;
    color: ${colorFunctionalGray70};
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const ProjectCountBadge = styled.span`
    flex-shrink: 0;
    font-size: 11px;
    color: ${colorFunctionalGray50};
    background: ${colorFunctionalGray20};
    border-radius: 10px;
    padding: 1px 6px;
    margin-left: ${spacingXs};
`

// ---------------------------------------------------------------------------
// SubgroupTreeNode
// ---------------------------------------------------------------------------

/**
 * Renders a single group row in the subgroup selection tree, recursively rendering
 * all of its children with depth-based left indentation.
 *
 * Parameters:
 * group (iCompanyGroup): The group to render.
 * allGroups (Record<number, iCompanyGroup>): All groups keyed by ID.
 * depth (number): Nesting depth; 0 = direct child of source group.
 * subgroupIds (Set<number>): Currently selected "update group" IDs.
 * subgroupIdsForProjects (Set<number>): Currently selected "include projects" IDs.
 * onToggleSubgroupId ((id, checked) => void): Toggle handler for subgroupIds.
 * onToggleSubgroupForProjects ((id, checked) => void): Toggle handler for subgroupIdsForProjects.
 */
const SubgroupTreeNode: FC<SubgroupTreeNodeProps> = memo(({ group, allGroups, depth, subgroupIds, subgroupIdsForProjects, onToggleSubgroupId, onToggleSubgroupForProjects }) => {
    const [isCollapsed, setIsCollapsed] = useState(false)

    const children = useMemo(
        () =>
            (group.children_ids ?? [])
                .map((id: number) => allGroups[id])
                .filter((g: iCompanyGroup | undefined): g is iCompanyGroup => g != null)
                .sort((a: iCompanyGroup, b: iCompanyGroup) => a.name.localeCompare(b.name)),
        [group.children_ids, allGroups]
    )

    const hasChildren = children.length > 0
    const projectCount = group.project_ids?.length ?? 0

    return (
        <>
            <TreeRow data-testid="subgroup-row">
                <TreeNameCell style={{ paddingLeft: depth * TREE_INDENT_PX }}>
                    <ExpandToggle
                        $expanded={!isCollapsed}
                        onClick={hasChildren ? () => setIsCollapsed(c => !c) : undefined}
                        aria-label={hasChildren ? (isCollapsed ? "Expand" : "Collapse") : undefined}
                        style={{ visibility: hasChildren ? "visible" : "hidden" }}
                    />
                    <GroupNameText title={group.name}>{group.name}</GroupNameText>
                    {projectCount > 0 && (
                        <ProjectCountBadge>
                            {projectCount} {projectCount === 1 ? "project" : "projects"}
                        </ProjectCountBadge>
                    )}
                </TreeNameCell>
                <TreeCheckboxCell>
                    <Checkbox id={`subgroup-update-${group.id}`} checked={subgroupIds.has(group.id)} onChange={() => onToggleSubgroupId(group.id, !subgroupIds.has(group.id))} />
                </TreeCheckboxCell>
                <TreeCheckboxCell>
                    <Checkbox
                        id={`subgroup-projects-${group.id}`}
                        checked={subgroupIdsForProjects.has(group.id)}
                        onChange={() => onToggleSubgroupForProjects(group.id, !subgroupIdsForProjects.has(group.id))}
                    />
                </TreeCheckboxCell>
            </TreeRow>
            <ChildrenWrapper $expanded={!isCollapsed}>
                <ChildrenInner>
                    {children.map((child: iCompanyGroup) => (
                        <SubgroupTreeNode
                            key={child.id}
                            group={child}
                            allGroups={allGroups}
                            depth={depth + 1}
                            subgroupIds={subgroupIds}
                            subgroupIdsForProjects={subgroupIdsForProjects}
                            onToggleSubgroupId={onToggleSubgroupId}
                            onToggleSubgroupForProjects={onToggleSubgroupForProjects}
                        />
                    ))}
                </ChildrenInner>
            </ChildrenWrapper>
        </>
    )
})

export default SubgroupTreeNode
